import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import {
  OngoingConflictAppointment,
  OngoingResolution,
  patientNameFromOngoingItem,
} from "./ongoingRoomGuard";

type Props = {
  open: boolean;
  roomNumber: string;
  others: OngoingConflictAppointment[];
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (
    resolutions: Array<{
      appointment: OngoingConflictAppointment;
      status: OngoingResolution;
    }>
  ) => void;
};

export default function OngoingRoomConflictModal({
  open,
  roomNumber,
  others,
  saving = false,
  onCancel,
  onConfirm,
}: Props) {
  const [choices, setChoices] = useState<Record<string, OngoingResolution>>(
    {}
  );

  useEffect(() => {
    if (!open) return;
    const next: Record<string, OngoingResolution> = {};
    for (const row of others) {
      next[String(row._id)] = "completed";
    }
    setChoices(next);
  }, [open, others]);

  const allChosen = useMemo(
    () => others.every((row) => Boolean(choices[String(row._id)])),
    [others, choices]
  );

  if (!open) return null;

  const body = (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ongoing-room-conflict-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div>
            <h2
              id="ongoing-room-conflict-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              Another patient is still ongoing
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Room {roomNumber} already has{" "}
              {others.length === 1 ? "an ongoing patient" : "ongoing patients"}.
              Mark {others.length === 1 ? "them" : "each"} as completed or on
              hold before setting this appointment to ongoing.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="px-5 pb-2 space-y-3 max-h-[50vh] overflow-y-auto">
          {others.map((row) => {
            const id = String(row._id);
            const choice = choices[id] ?? "completed";
            return (
              <div
                key={id}
                className="rounded-xl border border-slate-200 dark:border-slate-600 p-3"
              >
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {patientNameFromOngoingItem(row)}
                </div>
                {row?.queue != null && row?.queue !== "" ? (
                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                    Token {Number(row.queue)}
                  </div>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setChoices((prev) => ({ ...prev, [id]: "completed" }))
                    }
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      choice === "completed"
                        ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600"
                        : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    Completed
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setChoices((prev) => ({ ...prev, [id]: "waiting" }))
                    }
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      choice === "waiting"
                        ? "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600"
                        : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    On hold
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || !allChosen}
            onClick={() =>
              onConfirm(
                others.map((appointment) => ({
                  appointment,
                  status: choices[String(appointment._id)] ?? "completed",
                }))
              )
            }
            className="px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? "Updating…" : "Update & continue"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
